import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, RefreshCw } from "lucide-react";

import { HubDataTable, HubDataTextCell, type HubDataTableColumn } from "@/components/hub/HubDataTable";
import { Button } from "@/components/ui/Button";
import { Card, CardBody } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { PageTitleWithHelp } from "@/components/ui/title-help";
import { SearchableSelect, type SearchableSelectOption } from "@/components/ui/SearchableSelect";
import { ApiError } from "@/lib/api";
import { useHubModuleWrite } from "@/lib/useHubModuleWrite";
import { cn } from "@/lib/utils";

import {
  fetchWirelessAssignmentsWorkspace,
  formatWirelessAssignmentsClientOptionLabel,
  patchWirelessAssignmentsOrganizationClient,
  WIRELESS_ASSIGNMENTS_QUERY_KEY,
  type WirelessAssignmentsOrganizationRow,
  type WirelessAssignmentsUserContactRow,
  type WirelessAssignmentsWorkspaceResponse,
} from "./wirelessAssignmentsApi";

type ActiveTab = "organizations" | "user-contacts";
type OrganizationSortKey = "name" | "domain" | "client_label" | "group_name";
type UserContactSortKey = "first_name" | "last_name" | "contact_email" | "email_list" | "organization_name";

const PAGE_HELP =
  "Map wireless portal organizations to Freshsales customers by setting each organization's Freshsales account id. Default New Devices group is shown read-only from the portal directory.";

function InlineAssignmentSelect({
  value,
  options,
  disabled,
  placeholder,
  onChange,
}: {
  value: string;
  options: SearchableSelectOption[];
  disabled: boolean;
  placeholder: string;
  onChange: (nextValue: string) => void;
}) {
  const [search, setSearch] = useState("");
  const selectedLabel = options.find((option) => option.value === value)?.label ?? null;

  return (
    <SearchableSelect
      presentation="panelSearch"
      value={value}
      selectedLabelOverride={selectedLabel}
      onValueChange={(option) => {
        onChange(option?.value ?? "");
        setSearch("");
      }}
      options={options}
      searchValue={search}
      onSearchChange={setSearch}
      placeholder={placeholder}
      searchPlaceholder="Search..."
      emptyMessage="No matches."
      disabled={disabled}
      panelMinWidth={280}
    />
  );
}

function OrganizationsTab({
  workspace,
  writesAllowed,
  search,
}: {
  workspace: WirelessAssignmentsWorkspaceResponse;
  writesAllowed: boolean;
  search: string;
}) {
  const queryClient = useQueryClient();
  const [pendingOrgId, setPendingOrgId] = useState<number | null>(null);

  const clientOptions = useMemo<SearchableSelectOption[]>(() => {
    const unassigned: SearchableSelectOption = { value: "", label: "Unassigned" };
    const rows = workspace.clients.map((client) => ({
      value: String(client.fs_account_id),
      label: formatWirelessAssignmentsClientOptionLabel(client),
    }));
    return [unassigned, ...rows];
  }, [workspace.clients]);

  const assignClientMutation = useMutation({
    mutationFn: async (input: { organizationId: number; fsAccountId: number | null }) =>
      patchWirelessAssignmentsOrganizationClient(input.organizationId, input.fsAccountId),
    onMutate: ({ organizationId }) => {
      setPendingOrgId(organizationId);
    },
    onSuccess: (payload) => {
      queryClient.setQueryData<WirelessAssignmentsWorkspaceResponse>(WIRELESS_ASSIGNMENTS_QUERY_KEY, (current) =>
        current == null
          ? current
          : {
              ...current,
              organizations: payload.organizations,
              clients: payload.clients,
            },
      );
    },
    onSettled: () => {
      setPendingOrgId(null);
    },
  });

  const filteredRows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (needle === "") {
      return workspace.organizations;
    }
    return workspace.organizations.filter((row) => {
      const haystack = [row.name, row.domain, row.client_label, row.group_name]
        .filter((value) => value != null && value !== "")
        .join(" ")
        .toLowerCase();
      return haystack.includes(needle);
    });
  }, [search, workspace.organizations]);

  const organizationColumns = useMemo((): HubDataTableColumn<
    WirelessAssignmentsOrganizationRow,
    OrganizationSortKey
  >[] => {
    return [
      {
        id: "name",
        header: "Organization",
        sortKey: "name",
        sortValue: (row) => row.name,
        cell: (row) => <span className="font-medium text-slate-900">{row.name}</span>,
      },
      {
        id: "domain",
        header: "Domain",
        sortKey: "domain",
        sortValue: (row) => row.domain ?? "",
        cell: (row) => row.domain?.trim() || "—",
      },
      {
        id: "client_label",
        header: "Customer",
        sortKey: "client_label",
        sortValue: (row) => row.client_label ?? "",
        cellClassName: "min-w-[18rem]",
        cell: (row) => {
          const rowBusy = pendingOrgId === row.organization_id;
          return (
            <InlineAssignmentSelect
              value={row.client_fs_account_id == null ? "" : String(row.client_fs_account_id)}
              options={clientOptions}
              disabled={!writesAllowed || rowBusy || assignClientMutation.isPending}
              placeholder="Select customer"
              onChange={(nextValue) => {
                const fsAccountId = nextValue.trim() === "" ? null : Number.parseInt(nextValue, 10);
                void assignClientMutation.mutateAsync({
                  organizationId: row.organization_id,
                  fsAccountId: Number.isFinite(fsAccountId) ? fsAccountId : null,
                });
              }}
            />
          );
        },
      },
      {
        id: "group_name",
        header: "New Devices group",
        sortKey: "group_name",
        sortValue: (row) => row.group_name ?? "",
        cellClassName: "min-w-[14rem]",
        cell: (row) => row.group_name?.trim() || <span className="text-slate-400">—</span>,
      },
    ];
  }, [assignClientMutation, clientOptions, pendingOrgId, writesAllowed]);

  return (
    <div className="space-y-3">
      <HubDataTable
        chrome="extraToolbar"
        title="Organizations"
        columns={organizationColumns}
        rows={filteredRows}
        rowKey={(row) => String(row.organization_id)}
        empty="No wireless portal organizations found."
        isFiltered={search.trim() !== ""}
        filteredEmpty="No rows match these filters."
      />
      {!writesAllowed ? (
        <p className="text-sm text-slate-500">Your role has read-only access to Manufacturing management.</p>
      ) : null}
    </div>
  );
}

function UserContactsTab({
  workspace,
  search,
}: {
  workspace: WirelessAssignmentsWorkspaceResponse;
  search: string;
}) {
  const filteredRows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (needle === "") {
      return workspace.user_contacts;
    }
    return workspace.user_contacts.filter((row) => {
      const haystack = [
        row.first_name,
        row.last_name,
        row.contact_email,
        row.email_list,
        row.organization_name,
      ]
        .filter((value) => value != null && value !== "")
        .join(" ")
        .toLowerCase();
      return haystack.includes(needle);
    });
  }, [search, workspace.user_contacts]);

  const userContactColumns = useMemo((): HubDataTableColumn<
    WirelessAssignmentsUserContactRow,
    UserContactSortKey
  >[] => {
    return [
      {
        id: "first_name",
        header: "First name",
        sortKey: "first_name",
        sortValue: (row) => row.first_name ?? "",
        cell: (row) => row.first_name?.trim() || "—",
      },
      {
        id: "last_name",
        header: "Last name",
        sortKey: "last_name",
        sortValue: (row) => row.last_name ?? "",
        cell: (row) => row.last_name?.trim() || "—",
      },
      {
        id: "contact_email",
        header: "Contact email",
        sortKey: "contact_email",
        overflow: "truncate",
        sortValue: (row) => row.contact_email ?? "",
        cell: (row) => <HubDataTextCell overflow="truncate" value={row.contact_email?.trim() ?? "—"} />,
      },
      {
        id: "email_list",
        header: "Email list",
        sortKey: "email_list",
        overflow: "truncate",
        sortValue: (row) => row.email_list ?? "",
        cell: (row) => <HubDataTextCell overflow="truncate" value={row.email_list?.trim() ?? "—"} />,
      },
      {
        id: "organization_name",
        header: "Organization",
        sortKey: "organization_name",
        sortValue: (row) => row.organization_name ?? "",
        cell: (row) => row.organization_name?.trim() || "—",
      },
    ];
  }, []);

  return (
    <HubDataTable
      chrome="extraToolbar"
      title="User contacts"
      columns={userContactColumns}
      rows={filteredRows}
      rowKey={(row) =>
        [row.contact_email, row.first_name, row.last_name, row.organization_name].filter(Boolean).join("|") ||
        "contact"
      }
      empty="No wireless portal user contacts found."
      isFiltered={search.trim() !== ""}
      filteredEmpty="No rows match these filters."
    />
  );
}

export function WirelessAssignmentsPage() {
  const writesAllowed = useHubModuleWrite("manufacturing_management");
  const [activeTab, setActiveTab] = useState<ActiveTab>("organizations");
  const [search, setSearch] = useState("");

  const workspaceQuery = useQuery({
    queryKey: WIRELESS_ASSIGNMENTS_QUERY_KEY,
    queryFn: fetchWirelessAssignmentsWorkspace,
    staleTime: 30_000,
  });

  const errorMessage =
    workspaceQuery.error instanceof ApiError
      ? workspaceQuery.error.message
      : workspaceQuery.error instanceof Error
        ? workspaceQuery.error.message
        : null;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PageTitleWithHelp hoverHint={PAGE_HELP}>Wireless assignments</PageTitleWithHelp>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          disabled={workspaceQuery.isFetching}
          onClick={() => {
            void workspaceQuery.refetch();
          }}
        >
          {workspaceQuery.isFetching ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
          Refresh
        </Button>
      </div>

      {workspaceQuery.isLoading ? (
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading wireless portal directory...
        </div>
      ) : null}

      {errorMessage != null ? (
        <Card>
          <CardBody>
            <p className="text-sm text-red-700">{errorMessage}</p>
          </CardBody>
        </Card>
      ) : null}

      {workspaceQuery.data != null ? (
        <>
          <div className="flex flex-wrap items-end gap-3">
            <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1">
              {(
                [
                  ["organizations", "Organizations"],
                  ["user-contacts", "User contacts"],
                ] as const
              ).map(([tabId, label]) => (
                <button
                  key={tabId}
                  type="button"
                  className={cn(
                    "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                    activeTab === tabId ? "bg-brand-700 text-white" : "text-slate-700 hover:bg-slate-100",
                  )}
                  onClick={() => setActiveTab(tabId)}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="min-w-[16rem] flex-1">
              <Input
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder={activeTab === "organizations" ? "Search organizations..." : "Search user contacts..."}
              />
            </div>
            <p className="text-xs text-slate-500">
              Portal source: {workspaceQuery.data.read_source === "live_rds" ? "live Wireless_Prod" : "Supabase mirror"}
            </p>
          </div>

          {activeTab === "organizations" ? (
            <OrganizationsTab workspace={workspaceQuery.data} writesAllowed={writesAllowed} search={search} />
          ) : (
            <UserContactsTab workspace={workspaceQuery.data} search={search} />
          )}
        </>
      ) : null}
    </div>
  );
}
